import {computed, inject, Injectable, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CookieService} from "ngx-cookie-service";
import {
  combineLatest,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap, tap,
} from "rxjs";
import {BehaviorSubject, Subject} from "rxjs";
import {ApiService, Tables} from "src/app/services/api.service";
import {SelectionService} from "src/app/services/selection.service";
import {UserService} from "src/app/services/user.service";
import {Category} from "src/app/shared/interfaces/category";
import {Malfunction} from "src/app/shared/interfaces/malfunction";
import {ProductionLine} from "src/app/shared/interfaces/productionLine";
import {Solution} from "src/app/shared/interfaces/solution";
import {Userrole} from "src/app/shared/interfaces/user";
import {Workstation} from "src/app/shared/interfaces/workstation";
import {TreeNode} from "../kd-workplace/kd-workplace.component";
import {HeaderService} from "src/app/services/header.service";
import {EventService} from "src/app/services/event.service";
import {ActionType} from "./edit-tree-item.service";
import {SolutionService} from "./solution.service";
import {Router} from "@angular/router";
import {ReportService} from "../../error-report/data-access/report.service";

interface ExpandedNodes {
  productionLine: TreeNode | null;
  workplace: TreeNode | null;
  category: TreeNode | null;
  malfunction: TreeNode | null;
  solution: TreeNode | null;
}

interface KnowledgeBaseState {
  selectedProductionLine: string | null;
  selectedWorkplace: string | null;
  selectedCategory: string | null;
  selectedMalfunction: string | null;
  selectedSolution: string | null;
  workplaceSelectionData: ProductionLine[] | Workstation[];
  treeDataSource: any;
  expandedNodes: ExpandedNodes;
  expandedNodeIds: string[];
}

export type NodeType =
  | "productionLine"
  | "workplace"
  | "category"
  | "malfunction"
  | "solution";

@Injectable({
  providedIn: "root",
})
export class KnowledgeBaseService {
  apiService = inject(ApiService);
  eventService = inject(EventService);
  userService = inject(UserService);
  selectionService = inject(SelectionService);
  solutionService = inject(SolutionService);
  reportService = inject(ReportService);
  headerService = inject(HeaderService);
  cookieService = inject(CookieService);
  router = inject(Router);

  private Userroles = Userrole;

  public treeInputLevel = signal<0 | 1 | 2>(0);

  public add$ = new Subject<{ item: Partial<TreeNode>; level: ActionType }>();
  public update$ = new Subject<TreeNode>();

  public resetStateOnLogout$ = this.eventService.logout$;
  public initializeTrigger$ = new Subject<void>();

  public productionLine =
    this.userService.user()?.app_metadata?.["productionLine"];
  private selectedLevel = new BehaviorSubject<number>(1);
  private selectedWorkplace$ = new Subject<string>();
  private selectedCategory$ = new Subject<string>();
  private selectedMalfunction$ = new Subject<string>();
  private productionLines$: Observable<ProductionLine[]> =
    this.initializeTrigger$.pipe(
      switchMap(() => this.getProductionLines())
    )
  private workplaces$ = combineLatest([
    this.initializeTrigger$,
    this.selectionService.currentProductionLine
  ]).pipe(
    switchMap(([_, productionLine]) => {
      if (productionLine) {
        return this.getWorkplacesForProductionLine(productionLine);
      }
      return of([]);
    })
  );

  workplaceSelectionData$: Observable<ProductionLine[] | Workstation[]> =
    combineLatest([this.productionLines$, this.workplaces$]).pipe(
      map(([productionLines, workplaces]) => {
        if (
          (this.userService.userrole() === Userrole.Produktionsleiter ||
            this.userService.userrole() === Userrole.Techniker) &&
          !this.state().selectedProductionLine
        ) {
          return productionLines;
        } else {
          return workplaces;
        }
      })
    );
  private treeDataSubject$ = new BehaviorSubject<TreeNode[]>([]);
  treeData$ = this.treeDataSubject$.asObservable();

  private readonly initialState: KnowledgeBaseState = {
    selectedProductionLine: "",
    selectedWorkplace: "",
    selectedCategory: "",
    selectedMalfunction: "",
    selectedSolution: "",
    workplaceSelectionData: [],
    treeDataSource: [],
    expandedNodeIds: [],
    expandedNodes: {
      productionLine: null,
      workplace: null,
      category: null,
      malfunction: null,
      solution: null,
    },
  };

  private state = signal<KnowledgeBaseState>(this.initialState);

  selectedProductionLine = computed(() => this.state().selectedProductionLine);
  selectedWorkplace = computed(() => this.state().selectedWorkplace);
  selectedCategory = computed(() => this.state().selectedCategory);
  selectedMalfunction = computed(() => this.state().selectedMalfunction);
  workplaceSelectionData = computed(() => this.state().workplaceSelectionData);
  treeDataSource = computed(() => this.state().treeDataSource);
  expandedNodes = computed(() => this.state().expandedNodes);
  expandedNodeIdsSignal = computed(() => {
    const keys: (keyof ExpandedNodes)[] = [
      "category",
      "malfunction",
      "solution",
    ];
    return keys
      .map((key) => this.state().expandedNodes[key]?.id!)
      .filter((id): id is string => id !== undefined);
  });
  expandedNodesPath = computed(() => {
    return [
      this.state().expandedNodes.productionLine?.name,
      this.state().expandedNodes.workplace?.name,
      this.state().expandedNodes.category?.name,
      this.state().expandedNodes.malfunction?.name,
    ]
      .filter((item) => item != null)
      .join(" / ");
  });
  activePage = signal<number>(0);
  activeModule = signal<"knowledgeBase" | "reportDowntime" | "downtimeDetails">(
    "knowledgeBase"
  );

  constructor() {
    if (this.userService.userrole() === Userrole.Mitarbeiter) {
      const productionLine = {
        id: this.cookieService.get("productionLine")?.split("%")[0],
        name: this.cookieService.get("productionLine")?.split("%")[1],
      };
      this.selectionService.changeProductionLine(productionLine.id);
      this.updateSelectedItems("productionLine", {
        id: productionLine.id,
        name: productionLine.name,
        type: "productionLine",
      });
    } else if (
      this.userService.userrole() === Userrole.Teamleiter ||
      this.userService.userrole() === Userrole.Schichtleiter
    ) {
      this.selectionService.changeProductionLine(
        this.userService.user()?.app_metadata?.["productionLine"].split("%")[0]
      );
      this.updateSelectedItems("productionLine", {
        id: this.userService
          .user()
          ?.app_metadata?.["productionLine"].split("%")[0],
        name: this.userService
          .user()
          ?.app_metadata?.["productionLine"].split("%")[1],
        type: "productionLine",
      });
    }

    this.resetStateOnLogout$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.resetState();
      this.activePage.set(0);
    });

    combineLatest([
      this.selectionService.currentProductionLine,
      this.selectionService.currentWorkplace,
      this.workplaceSelectionData$,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([productionLine, workplace, workplaceSelectionData]) => {
        this.state.update((state) => ({
          ...state,
          selectedProductionLine: productionLine,
          selectedWorkplace: workplace,
          workplaceSelectionData: workplaceSelectionData,
        }));
      });

    this.selectedWorkplace$
      .pipe(
        takeUntilDestroyed(),
        switchMap((workplaceId) =>
          this.getCategoriesForWorkplace(workplaceId).pipe(
            map((categories) => this.transformToTreeNodes(categories))
          )
        )
      )
      .subscribe((treeNodes) => {
        this.treeDataSubject$.next(treeNodes);
        this.state.update((state) => ({
          ...state,
          treeDataSource: treeNodes,
        }));
      });

    this.selectedCategory$
      .pipe(
        takeUntilDestroyed(),
        switchMap((categoryId) =>
          this.getMalfunctionsForCategory(categoryId).pipe(
            map((malfunctions) => ({
              categoryId,
              malfunctions: this.transformToTreeNodes(malfunctions),
            }))
          )
        )
      )
      .subscribe(({categoryId, malfunctions}) => {
        const parentNode = this.findNode(
          this.state().treeDataSource,
          categoryId
        );
        if (parentNode && malfunctions) {
          this.treeDataSubject$.next(
            this.treeDataSource().map((node: TreeNode) =>
              node.id === categoryId
                ? {
                  ...node,
                  children: malfunctions,
                }
                : node
            )
          );
          this.state.update((state) => ({
            ...state,
            treeDataSource: this.treeDataSource().map((node: TreeNode) =>
              node.id === categoryId
                ? {
                  ...node,
                  children: malfunctions,
                }
                : node
            ),
          }));
        }
      });

    this.selectedMalfunction$
      .pipe(
        switchMap((malfunctionId) =>
          this.getSolutionsForMalfunction(malfunctionId).pipe(
            map(solutions => solutions.filter((solution) => solution.name)),
            map((solutions) => ({
              malfunctionId,
              solutions: this.transformToTreeNodes(solutions),
            }))
          )
        ),
        takeUntilDestroyed(),
        shareReplay(1)
      )
      .subscribe(({malfunctionId, solutions}) => {
        const parentCategory = this.findNode(
          this.state().treeDataSource,
          this.state().expandedNodes.category?.id!
        );
        if (parentCategory) {
          const parentMalfunction = this.findNode(
            parentCategory.children!,
            malfunctionId
          );
          if (parentMalfunction) {
            const updatedData = this.updateSolutionsForMalfunctions(
              parentCategory.id,
              parentMalfunction.id,
              solutions
            );
            this.treeDataSubject$.next(updatedData);
            this.state.update((state) => ({
              ...state,
              treeDataSource: updatedData,
            }));
          }
        }
      });

    this.add$
      .pipe(
        takeUntilDestroyed(),
        switchMap(({item, level}) => this.addItem(item, level))
      )
      .subscribe((newItem) => {
        // newItem is level 0 - category
        if (newItem && this.treeInputLevel() === 0) {
          const updatedData = [
            ...this.treeDataSource().filter(
              (node: TreeNode) => node.type !== "button"
            ),
            this.transformToTreeNodes([...[], newItem])[0],
          ];
          this.updateTreeDataSource(updatedData);
        } else if (
          // newItem is level 1 - malfunction
          newItem &&
          this.treeInputLevel() === 1
        ) {
          const updatedData = this.treeDataSource().map((node: TreeNode) => {
            if (node.id === this.state().expandedNodes.category?.id) {
              return {
                ...node,
                children: [
                  ...(node.children?.filter(
                    (node: TreeNode) => node.type !== "button"
                  ) || []),
                  this.transformToTreeNodes([...[], newItem])[0],
                ],
              };
            }
            return node;
          });
          this.updateTreeDataSource(updatedData);
        } else if (
          // newItem is level 2 - solution
          newItem && this.treeInputLevel() === 2) {
          const updatedData = this.treeDataSource().map((node: TreeNode) => {
            if (node.id === this.state().expandedNodes.category?.id) {
              return {
                ...node,
                children: (node.children || []).map((child) => {
                  if (child.id === this.state().expandedNodes.malfunction?.id) {
                    return {
                      ...child,
                      children: [
                        ...(child.children?.filter(
                          (node: TreeNode) => node.type !== "button"
                        ) || []),
                        this.transformToTreeNodes([...[], newItem])[0],
                      ],
                    };
                  }
                  return child;
                }),
              };
            }

            return node;
          });
          this.updateTreeDataSource(updatedData);
          this.reportService.update$.next({
            properties: {
              solution: newItem.id
            }
          })
          this.solutionService.selectedSolutionId$.next(newItem.id);
          this.solutionService.editSolution.set(true);
          this.changeActivePage(2);
        }
      });

    this.update$
      .pipe(switchMap((item) => this.updateItem(item)))
      .subscribe((updatedItem) => {
        // updated item is level 0 - category
        if (updatedItem) {
          const updatedTreeData = this.updateTreeNode(
            this.treeDataSource(),
            this.transformToTreeNodes(updatedItem)[0]
          );
          this.updateTreeDataSource(updatedTreeData);
        }
      });
  }

  updateSolutionsForMalfunctions(
    categoryId: string,
    malfunctionId: string,
    newChildren: TreeNode[]
  ) {
    return this.treeDataSource().map((item: TreeNode) => {
      if (item.id === categoryId) {
        return {
          ...item,
          children: item.children?.map((child: TreeNode) => {
            if (child.id === malfunctionId) {
              return {
                ...child,
                children: newChildren,
              };
            }
            return child;
          }),
        };
      }
      return item;
    });
  }

  addItem(
    item: Partial<Category | Malfunction | Solution>,
    level: ActionType
  ): Observable<any> {
    switch (level) {
      case "addCategory":
        return this.apiService.addItemToKnowledgeBase(Tables.Category, {
          name: item.name,
          workplace: this.selectedWorkplace(),
        });
      case "addMalfunction":
        return this.apiService.addItemToKnowledgeBase(Tables.Malfunction, {
          name: item.name,
          description: item.description,
          workplace: this.expandedNodes().workplace?.id,
          category: this.expandedNodes().category?.id,
        });
      case "addSolution":
        return this.apiService.addItemToKnowledgeBase(Tables.Solution, {
          name: item.name,
          description: item.description || 'Bitten geben Sie eine Beschreibung ein',
          malfunction: this.activeModule() === 'knowledgeBase'
            ? this.expandedNodes().malfunction?.id
            : this.reportService.currentReport()?.malfunction,
        })
      default:
        return of(null);
    }
  }

  getRelation(item: TreeNode): Tables | null {
    switch (item.type) {
      case "category":
        return Tables.Category;
      case "malfunction":
        return Tables.Malfunction;
      case "solution":
        return Tables.Solution;
      default:
        return null
    }
  }

  updateItem(item: TreeNode) {
    let relation = this.getRelation(item)
    const updateValues = {
      id: item.id,
      name: item.name,
    };
    if (relation) {
      return this.apiService.updateItemInKnowledgeBase(relation, updateValues);
    } else {
      return of(null)
    }
  }

  deleteItem(item: TreeNode) {
    let relation = this.getRelation(item);
    if (relation) {
      return this.checkForChildren(item).pipe(
        switchMap(hasChildren => {
          if (hasChildren) {
            throw new Error('Element hat untergeordnete Elemente und kann nicht gelöscht werden.');
          }
          return this.apiService.deleteItemFromKnowledgeBase(relation, item.id);
        }),
        tap(deletedItem => {
          if (deletedItem) {
            const updatedTreeData = this.deleteTreeNode(this.treeDataSource(), item.id);
            this.updateTreeDataSource(updatedTreeData);
          }
        })
      );
    } else {
      return of(null)
    }
  }

  checkForChildren(item: TreeNode): Observable<boolean> {
    switch (item.type) {
      case 'category':
        return this.getMalfunctionsForCategory(item.id).pipe(
          map(malfunctions => malfunctions.length > 0)
        );
      case 'malfunction':
        return this.getSolutionsForMalfunction(item.id).pipe(
          map(malfunctions => malfunctions.length > 0)
        );
      case 'solution':
        return this.apiService.getReportsBySolutionId(item.id).pipe(
          map(reports => reports.length > 0)
        )
      default:
        return of(false);
    }
  }

  updateTreeNode(tree: TreeNode[], updatedItem: TreeNode): TreeNode[] {
    return tree.map((node) => {
      if (node.id === updatedItem.id) {
        return {
          ...node,
          name: updatedItem.name,
        };
      }
      if (node.children) {
        return {
          ...node,
          children: this.updateTreeNode(node.children, updatedItem),
        };
      }
      return node;
    });
  }

  deleteTreeNode(tree: TreeNode[], id: string): TreeNode[] {
    return tree.flatMap((node) => {
      if (node.id === id) {
        return [];
      }

      if (node.children) {
        return {
          ...node,
          children: this.deleteTreeNode(node.children, id),
        };
      }
      return node;
    });
  }

  updateTreeDataSource(updatedData: TreeNode[]) {
    this.treeDataSubject$.next(updatedData);
    this.state.update((state) => ({
      ...state,
      treeDataSource: updatedData,
    }));
  }

  hasAccessToLevel(level: number): boolean {
    switch (this.userService.userrole()) {
      case this.Userroles.Produktionsleiter:
      case this.Userroles.Techniker:
        return level >= 1;
      case this.Userroles.Teamleiter:
      case this.Userroles.Schichtleiter:
      case this.Userroles.Mitarbeiter:
        return level >= 2;
      default:
        return false;
    }
  }

  getProductionLines(): Observable<ProductionLine[]> {
    return this.apiService
      .getProductionLinesAdmin()
      .pipe(map((lines) => lines ?? []));
  }

  getWorkplacesForProductionLine(
    productionLineId: string
  ): Observable<Workstation[]> {
    return from(
      this.apiService.getWorkplacesForProductionLine(productionLineId)
    ).pipe(map((workstations) => workstations ?? []));
  }

  getCategoriesForWorkplace(workstationId: string): Observable<Category[]> {
    return from(this.apiService.getCategoriesForWorkplace(workstationId)).pipe(
      map((categories) => categories ?? [])
    );
  }

  getMalfunctionsForCategory(
    categoryId: string
  ): Observable<Malfunction[]> {
    return from(this.apiService.getMalfunctionsForCategory(categoryId)).pipe(
      map((malfunctions) => malfunctions ?? [])
    );
  }

  getSolutionsForMalfunction(malfunctionId: string): Observable<Solution[]> {
    if (malfunctionId) {
      return from(
        this.apiService.getSolutionsForMalfunction(malfunctionId)
      ).pipe(map((solutions) => solutions ?? []));
    } else {
      return of([]);
    }
  }

  private transformToTreeNodes(
    responseArray: Category[] | Malfunction[] | Solution[]
  ): TreeNode[] {
    return responseArray.map((item: any) => ({
      id: item.id,
      name: item.name,
      type:
        "workplace" in item && !("category" in item)
          ? "category"
          : "category" in item
            ? "malfunction"
            : "solution",
      level:
        "workplace" in item && !("category" in item)
          ? 0
          : "category" in item
            ? 1
            : 2,
      expandable: true,
      children: [],
      data: item,
    }));
  }

  private findNode(nodes: TreeNode[], id: string): TreeNode | null {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = this.findNode(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  selectProductionLine(id: string, allowAccess?: boolean) {
    if (this.hasAccessToLevel(1) || allowAccess) {
      this.selectionService.changeProductionLine(id);
      this.selectionService.changeWorkplace("");
      this.selectedLevel.next(2);
    }
  }

  selectWorkplace(id: string | null) {
    if (this.hasAccessToLevel(2)) {
      this.selectedWorkplace$.next(id!);
      this.selectionService.changeWorkplace(id!);
      this.selectedLevel.next(3);
    }
  }

  selectCategory(id: string | null, expandedNodeIds: string[]) {
    if (this.hasAccessToLevel(3)) {
      this.state.update((state) => ({
        ...state,
        expandedNodeIds: expandedNodeIds,
      }));
      this.updateSelectedItems("malfunction", null);
      this.selectedCategory$.next(id!);
      this.selectionService.changeCategory(id);
      this.selectionService.changeMalfunction(null);
      this.selectedLevel.next(4);
    }
  }

  selectMalfunction(id: string | null, expandedNodeIds: string[]) {
    if (this.hasAccessToLevel(4)) {
      this.state.update((state) => ({
        ...state,
        expandedNodeIds: expandedNodeIds,
      }));
      this.selectedMalfunction$.next(id!);
      this.selectionService.changeMalfunction(id);
      this.selectedLevel.next(5);
    }
  }

  updateSelectedItems(level: NodeType, node: TreeNode | null) {
    this.state.update((state) => ({
      ...state,
      expandedNodes: {
        ...state.expandedNodes,
        [level]: node,
      },
    }));
  }

  resetSelectedItems() {
    this.selectionService.changeWorkplace(null);
    this.selectionService.changeCategory(null);
    this.selectionService.changeMalfunction(null);
    this.state.update((state) => ({
      ...state,
      expandedNodes: {
        ...state.expandedNodes,
        workplace: null,
        category: null,
        malfunction: null,
        solution: null,
      },
    }));
  }

  public resetCurrentLevel() {
    this.selectedLevel.next(0);
  }

  changeActivePage(number: number) {
    this.activePage.set(number);
  }

  initialize(): void {
    // Reset the selection service state
    this.activeModule.set("reportDowntime");
    this.resetState();
    this.changeActivePage(0);
    this.selectionService.changeProductionLine(null);
    this.selectionService.changeWorkplace(null);
    this.selectedLevel.next(1);

    // Trigger new data load
    this.initializeTrigger$.next();
  }

  resetState(propertiesToExclude: (keyof KnowledgeBaseState)[] = []): void {
    const preservedValues = Object.fromEntries(
      propertiesToExclude.map((prop) => [prop, this.state()[prop]])
    );

    this.state.set({
      ...this.initialState,
      ...preservedValues,
    });
  }

  goBack() {
    const currentLevel = this.selectedLevel.value;
    console.log(this.activePage(), currentLevel);
    switch (this.activePage()) {
      case 3:
        this.activePage.set(0);
        break;
      case 2:
        this.selectionService.changeSolution(null);
        this.updateSelectedItems("solution", null);
        this.changeActivePage(1);
        this.selectedLevel.next(currentLevel - 1);
        break;
      case 1:
        if (this.hasAccessToLevel(currentLevel)) {
          this.selectionService.changeMalfunction(null);
          this.selectionService.changeCategory(null);
          this.selectionService.changeWorkplace(null);
          this.updateSelectedItems("workplace", null);
          this.updateSelectedItems("category", null);
          this.updateSelectedItems("malfunction", null);
          this.selectedLevel.next(2);
          this.changeActivePage(0);
        }
        break;
      case 0:
        if (currentLevel == 1 || !this.hasAccessToLevel(currentLevel - 1)) {
          this.router.navigate(["/dashboard"]);
        }
        if (this.hasAccessToLevel(currentLevel)) {
          this.selectionService.changeProductionLine(null);
          this.selectedLevel.next(currentLevel - 1);
          this.headerService.setHeaderSubTitle("Übersicht");
          break;
        }
    }
  }
}
